import { EditOutlined, EyeOutlined, MergeCellsOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Descriptions, Flex, Skeleton, Space, Tooltip, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { getCustomer } from './api';
import { CasesTab } from './CasesTab';
import { ConsentTab } from './ConsentTab';
import { CustomerEditDrawer } from './CustomerEditDrawer';
import { InvoicesTab } from './InvoicesTab';
import { MergeCustomerDrawer } from './MergeCustomerDrawer';
import { RegistrationTab } from './RegistrationTab';
import { SalesTab } from './SalesTab';
import { WorkshopTab } from './WorkshopTab';
import { customerTypeLabel, stageLabel } from './stage';

const { Text } = Typography;

/** Tabs whose modules arrive later. Shown disabled so the spine is visible. */
const FUTURE_TABS: { key: string; labelKey: string; phase: number }[] = [
  { key: 'membership', labelKey: 'customers.tabs.membership', phase: 6 },
];

export function CustomerDetailPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid = '' } = useParams<{ uid: string }>();
  const [unmasked, setUnmasked] = useState(false);
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['customer', uid, unmasked],
    queryFn: () => getCustomer(uid, unmasked),
  });

  // An old link to a record that was merged away: follow it to the record that
  // holds the history now, and say so.
  const mergedIntoUid =
    error instanceof ApiError && error.code === 'CUSTOMER_MERGED'
      ? (error.details['mergedIntoUid'] as string | undefined)
      : undefined;
  useEffect(() => {
    if (mergedIntoUid !== undefined && error instanceof ApiError) {
      void message.info(error.message);
      setUnmasked(false);
      void navigate(`/customers/${mergedIntoUid}`, { replace: true });
    }
  }, [mergedIntoUid, error, navigate]);

  if (error !== null && mergedIntoUid === undefined) {
    return (
      <Card>
        <Flex vertical gap={12} align="flex-start">
          <Text type="danger">
            {error instanceof ApiError && error.code === 'ENTITY_NOT_FOUND'
              ? t('customers.notFound')
              : error instanceof ApiError
                ? error.message
                : t('customers.loadFailed')}
          </Text>
          <Button onClick={() => void navigate('/customers')}>{t('customers.backToList')}</Button>
        </Flex>
      </Card>
    );
  }

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const reveal = async (): Promise<void> => {
    setUnmasked(true);
    const result = await refetch();
    // A user without customer.view_pii still gets the masked value back, so say
    // so rather than leaving them staring at asterisks wondering if it failed.
    if (result.data?.idNumber?.startsWith('*') === true) {
      void message.info(t('customers.noPiiPermission'));
    }
  };

  const openCustomer = (other: string): void => {
    setEditing(false);
    void navigate(`/customers/${other}`);
  };

  return (
    <>
      <DetailPage
        title={data.name}
        subtitle={data.customerNo}
        tags={[{ label: stageLabel(data.lifecycleStage) }, { label: customerTypeLabel(data.type) }]}
        onBack={() => void navigate('/customers')}
        actions={
          // Shown to everyone: the API decides who may edit or merge, and its
          // refusal says what to do. Hiding the buttons would need a second copy
          // of the permission rules in the browser.
          <>
            <Button
              size="large"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('customers.edit.title')}
            </Button>
            <Button
              size="large"
              icon={<MergeCellsOutlined />}
              onClick={() => {
                setMerging(true);
              }}
            >
              {t('customers.merge.title')}
            </Button>
          </>
        }
        tabs={[
          {
            key: 'overview',
            label: t('customers.tabs.overview'),
            content: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('fields.phone')}>{data.phone}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.altPhone')}>{orDash(data.altPhone)}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.email')}>{orDash(data.email)}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.idType')}>{orDash(data.idType)}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.idNumber')}>
                    <Space>
                      <Text code>{orDash(data.idNumber)}</Text>
                      {data.idNumber !== null && !unmasked && (
                        <Tooltip title={t('customers.unmaskNote')}>
                          <Button size="small" icon={<EyeOutlined />} onClick={() => void reveal()}>
                            {t('customers.unmask')}
                          </Button>
                        </Tooltip>
                      )}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fields.tin')}>{orDash(data.tin)}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.address')} span={2}>
                    {orDash(
                      [data.addressLine1, data.addressLine2, data.postcode, data.city, data.state]
                        .filter((p) => p !== null && p.length > 0)
                        .join(', '),
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fields.source')}>
                    {orDash(data.sourceChannel)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('customers.added')}>
                    {formatDateTime(data.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fields.notes')} span={2}>
                    {orDash(data.notes)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'consents',
            label: t('customers.tabs.consents'),
            content: <ConsentTab customerUid={data.uid} />,
          },
          // Each module's tab reads that module's own list. antd mounts a tab's
          // content on first open, so nothing is fetched for a tab nobody opens.
          { key: 'sales', label: t('customers.tabs.sales'), content: <SalesTab customerUid={data.uid} /> },
          {
            key: 'registration',
            label: t('customers.tabs.registration'),
            content: <RegistrationTab customerUid={data.uid} />,
          },
          { key: 'workshop', label: t('customers.tabs.workshop'), content: <WorkshopTab customerUid={data.uid} /> },
          { key: 'invoices', label: t('customers.tabs.invoices'), content: <InvoicesTab customerUid={data.uid} /> },
          {
            key: 'cases',
            label: t('customers.tabs.cases'),
            content: <CasesTab customerUid={data.uid} customerName={data.name} />,
          },
          ...FUTURE_TABS.map((tab) => ({
            key: tab.key,
            label: t(tab.labelKey),
            disabled: true,
            content: <Card>{t('customers.comingIn', { phase: tab.phase })}</Card>,
          })),
        ]}
      />
      <CustomerEditDrawer
        customer={data}
        open={editing}
        onClose={() => {
          setEditing(false);
        }}
        onOpenMatch={openCustomer}
      />
      <MergeCustomerDrawer
        customer={data}
        open={merging}
        onClose={() => {
          setMerging(false);
        }}
        onMerged={(survivingUid) => {
          setMerging(false);
          void navigate(`/customers/${survivingUid}`);
        }}
      />
    </>
  );
}
